// RatingButton.jsx
import React, { useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import WithLivestreamRating from '../container/WithLivestreamRating.js';

import useUnmount from '../hook/useUnmount.js';

import { NULL_FUNCTION } from '../resource/defaults.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { ButtonId } from '../resource/mixpanel.js';

import { color } from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';

import StarOutlineIconSource from '../../img/ic-star-outline.svg';
import StarFillIconSource from '../../img/ic-star-fill.svg';

export const RatingButton = ({
  id = null,
  streamId = null,
  sessionId = null,
  hintDurationSeconds = 0,
  ratedAs = null,
  hintOpenedTimestamp = null,
  canRate = false,
  setHintOpenedTimestamp = NULL_FUNCTION,
  openRatingModal = NULL_FUNCTION,
}) => {
  const { t } = useTranslation();
  const [canOpenHint, setCanOpenHint] = useState(false);
  const hintTimerRef = useRef(null);
  const clickTrackingPayload = useMemo(() => {
    return {
      'stream.id': streamId,
      'stream.sessionId': sessionId,
    };
  }, [streamId, sessionId]);
  // open hint
  useEffect(() => {
    if (hintOpenedTimestamp == null && canRate) {
      if (hintTimerRef.current) {
        clearTimeout(hintTimerRef.current);
        hintTimerRef.current = null;
      }
      if (hintDurationSeconds > 0) {
        setHintOpenedTimestamp({ timestamp: Date.now() });
        setCanOpenHint(true);
        hintTimerRef.current = setTimeout(() => {
          setCanOpenHint(false);
        }, hintDurationSeconds * 1000);
      }
    }
  }, [
    canRate,
    hintDurationSeconds,
    hintOpenedTimestamp,
    setHintOpenedTimestamp,
  ]);
  useUnmount(() => {
    if (hintTimerRef.current) {
      clearTimeout(hintTimerRef.current);
    }
  });
  if (!canRate) return null;
  return (
    <StyledRatingButton>
      <CircleButton
        data-element_id={ButtonId.All.ButtonRating}
        data-tracking_payload={clickTrackingPayload}
        disabled={!canRate}
        onClick={() => {
          openRatingModal({ id });
        }}
      >
        <img
          src={ratedAs != null ? StarFillIconSource : StarOutlineIconSource}
          width={28}
          height={28}
          alt="rating"
        />
        {canOpenHint && (
          <RatingHintWrapper>
            <RatingHint>
              <img
                src={StarFillIconSource}
                width={16}
                height={16}
                alt="rating"
              />
              <RatingHintText>
                {t('user_livestream_rating_hint', {
                  ns: TranslationNamespace.LIVESTREAM,
                })}
              </RatingHintText>
            </RatingHint>
          </RatingHintWrapper>
        )}
      </CircleButton>
      {!!streamId && (
        <WithLivestreamRating id={streamId}>
          {({ displayRating, rating, isDisplayRatingEnabled }) =>
            !!rating && isDisplayRatingEnabled ? (
              <Rating>{displayRating}</Rating>
            ) : null
          }
        </WithLivestreamRating>
      )}
    </StyledRatingButton>
  );
};

RatingButton.propTypes = {
  id: PropTypes.string,
  streamId: PropTypes.string,
  sessionId: PropTypes.string,
  hintDurationSeconds: PropTypes.number,
  ratedAs: PropTypes.number,
  hintOpenedTimestamp: PropTypes.number,
  canRate: PropTypes.bool,
  setHintOpenedTimestamp: PropTypes.func,
  openRatingModal: PropTypes.func,
};

const StyledRatingButton = styled.div``;

const CircleButton = styled.button`
  ${ResetButtonStyle};
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(25, 25, 25, 0.2);
`;

const Rating = styled.div`
  margin-top: 2px;
  text-align: center;
  color: ${color.neutral[100]};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
`;

const RatingHintWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 0px;
  transform: translate(calc(-100% - 12px), -50%);
`;

const RatingHint = styled.div`
  position: relative;
  border-radius: 4px;
  padding: 6px 8px;
  display: flex;
  align-items: center;
  background-color: ${color.neutral[10]};
  > img {
    margin-right: 4px;
  }
  ::after {
    position: absolute;
    top: 50%;
    right: 0px;
    border-radius: 2px;
    width: 8px;
    height: 8px;
    background-color: ${color.neutral[10]};
    transform: translate(calc(50% - 1px), -50%) rotate(45deg);
    content: '';
  }
`;

const RatingHintText = styled.div`
  color: ${color.neutral[100]};
  font-size: 12px;
  line-height: 1.5;
  white-space: nowrap;
`;

export default RatingButton;
